import { useConfig, useOidcWithAdmin, useProduct } from '@revenuewell/front-end-bundle';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import FormsService from '../../services/forms-service/forms-service';
import InboxService from '../../services/inbox-service/inbox-service';
import { IMailboxWithLocation } from '../../services/mailbox-service/mailbox-service';
import PatientFeedbackService from '../../services/patient-feedback-service/patient-feedback-service';
import { useEventBus } from '../use-event-bus';
import { useLocations } from '../use-locations';
import { usePubnub } from '../use-pubnub/use-pubnub';

export const TodaysItemsContext = createContext({});

export const TodaysItemsProvider = (props: React.PropsWithChildren<any>) => {
  const { config } = useConfig();
  const { mailboxes } = usePubnub();
  const { hasProduct } = useProduct();
  const { selectedLocation } = useLocations();
  const { oidcService } = useOidcWithAdmin();
  const { publishToIframes, listen } = useEventBus();
  const formsService = useMemo(() => FormsService.getInstance(config, oidcService), [config, oidcService]);
  const inboxService = useMemo(() => InboxService.getInstance(config, oidcService), [config, oidcService]);
  const [forms2Migrated, setForms2Migrated] = useState(false);

  const checkIsMigrated = async (locationId: number) => {
    const isMigrated = await formsService.getIsMigratedToForms2(locationId);
    setForms2Migrated(isMigrated);
  };

  useEffect(() => {
    if (!selectedLocation?.id)
      return;
    checkIsMigrated(selectedLocation.id);
  }, [selectedLocation]);

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidcService),
    [config, oidcService]
  );

  const fetchUnreadFormsSubmissions = async () => {
    try {
      const url = `${config.practice.url}/Submissions/NewSubmissions`;
      const body = 'sort=SubmissionDate-desc&page=1&pageSize=25&group=&filter=';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        credentials: 'include',
        body
      });

      const data = (await response.json()) as { Total: number };
      return data.Total;
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const checkForms2Unread = async (locationId: number) => {
    try {
      if (!formsService) return 0;

      return await formsService.getFormsUnreadCount(locationId);
    } catch (error) {
      console.error(error);
      return 0;
    }
  };

  const getUnreadReviewsCount = async (locationId: number) => {
    try {
      return await patientFeedbackService.getUnreadReviewsCount(locationId);
    } catch (error) {
      console.error('Error fetching unread reviews count:', error);
      return 0;
    }
  };

  const fetchUnreadMessages = async (mboxes: IMailboxWithLocation[]) => {
    try {
      if (!mboxes.length) return 0;

      const locationMailboxes = mboxes.filter((d) => d.locationId === selectedLocation?.id);

      if (!locationMailboxes.length) return 0;

      const { total } = await inboxService.channelsUnreadCounts({
        mailboxIds: locationMailboxes.map((d) => d.id)
      });

      return total;
    } catch (error) {
      console.error('Error fetching unread messages:', error);

      return 0;
    }
  };

  const fetchStats = async (isForms2Migrated: boolean, locationId: number, mboxes: IMailboxWithLocation[]) => {
    const isForms = hasProduct('forms') && !isForms2Migrated;
    const isForms2 = hasProduct('forms2') && isForms2Migrated;
    const reviewsUnread = await getUnreadReviewsCount(locationId);
    const unreadMessages = await fetchUnreadMessages(mboxes);
    let formSubmissions = 0;

    if (isForms) formSubmissions = await fetchUnreadFormsSubmissions();

    if (isForms2) formSubmissions = await checkForms2Unread(locationId);

    publishToIframes({
      messageType: 'todays-items-change',
      data: {
        reviewsUnread,
        unreadMessages,
        formSubmissions,
        features: { forms: isForms, forms2: isForms2 }
      }
    });
  };

  useEffect(() => {
    if (!selectedLocation) return;

    return listen('todays-items-request', async () => {
      fetchStats(forms2Migrated, selectedLocation.id, mailboxes);
    });
  }, [selectedLocation, mailboxes]);

  useEffect(() => {
    if (!selectedLocation) return;

    const interval = setInterval(() => {
      fetchStats(forms2Migrated, selectedLocation.id, mailboxes);
    }, 120 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [selectedLocation, mailboxes]);

  return <TodaysItemsContext.Provider value={useMemo(() => ({}), [])}>{props.children}</TodaysItemsContext.Provider>;
};
